/** A run as a picture to post (Zalo, Facebook, Instagram story) or save to the phone's photos. */
export interface RunImageInput {
  /** Snapshot of the run's map (route over the territory grid). */
  map: HTMLCanvasElement;
  /** Which map library drew it, for the attribution line. */
  mapLib: 'mapbox' | 'maplibre';
  title: string;
  /** "Cong Dang · Today at 16:53 · Đà Nẵng" */
  subtitle: string;
  /** Up to six figures, shown in two rows of three. */
  stats: Array<{ label: string; value: string; unit?: string; color?: string }>;
  footer: string;
}

const W = 1080;
const H = 1350;
const INK = '#0a0e13';
const DISPLAY = '"Barlow Condensed", "Barlow", system-ui, sans-serif';
const SANS = '"Barlow", system-ui, sans-serif';

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Draws the share picture (1080×1350, Instagram's portrait size) and returns it as a PNG. */
export async function drawRunImage(input: RunImageInput): Promise<Blob> {
  await Promise.all([
    document.fonts?.load(`800 96px ${DISPLAY}`),
    document.fonts?.load(`700 48px ${DISPLAY}`),
    document.fonts?.load(`500 32px ${SANS}`),
  ]).catch(() => undefined);
  const logo = await loadImage('/brand/logo.png');

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);

  // The map fills the picture (cropped to cover), the route kept in the upper part.
  const mapH = 960;
  const m = input.map;
  const scale = Math.max(W / m.width, mapH / m.height);
  const dw = m.width * scale;
  const dh = m.height * scale;
  ctx.drawImage(m, (W - dw) / 2, (mapH - dh) / 2, dw, dh);

  // Fade into the panel holding the figures.
  const fade = ctx.createLinearGradient(0, mapH - 420, 0, mapH);
  fade.addColorStop(0, 'rgba(10,14,19,0)');
  fade.addColorStop(0.45, 'rgba(10,14,19,0.82)');
  fade.addColorStop(1, 'rgba(10,14,19,1)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, mapH - 420, W, 420);
  ctx.fillStyle = INK;
  ctx.fillRect(0, mapH, W, H - mapH);
  // Top shade so the logo stays readable over light map areas.
  const top = ctx.createLinearGradient(0, 0, 0, 200);
  top.addColorStop(0, 'rgba(10,14,19,0.75)');
  top.addColorStop(1, 'rgba(10,14,19,0)');
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, W, 200);

  if (logo) {
    const lh = 96;
    const lw = (logo.width / logo.height) * lh;
    ctx.drawImage(logo, 48, 40, lw, lh);
  }

  const pad = 64;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#f1f5f9';
  ctx.font = `800 84px ${DISPLAY}`;
  ctx.fillText(fit(ctx, input.title.toUpperCase(), W - pad * 2), pad, 790);
  ctx.fillStyle = '#9aa7b8';
  ctx.font = `500 34px ${SANS}`;
  ctx.fillText(fit(ctx, input.subtitle, W - pad * 2), pad, 842);

  // Figures: two rows of three.
  const colW = (W - pad * 2) / 3;
  input.stats.slice(0, 6).forEach((s, i) => {
    const x = pad + (i % 3) * colW;
    const y = 960 + Math.floor(i / 3) * 170;
    ctx.fillStyle = '#7f8ea3';
    ctx.font = `600 26px ${SANS}`;
    ctx.fillText(s.label.toUpperCase(), x, y);
    ctx.fillStyle = s.color ?? '#f1f5f9';
    ctx.font = `800 76px ${DISPLAY}`;
    ctx.fillText(s.value, x, y + 78);
    if (s.unit) {
      const vw = ctx.measureText(s.value).width;
      ctx.fillStyle = '#9aa7b8';
      ctx.font = `600 34px ${DISPLAY}`;
      ctx.fillText(s.unit, x + vw + 8, y + 78);
    }
  });

  ctx.fillStyle = '#c6ff3d';
  ctx.font = `700 34px ${DISPLAY}`;
  ctx.fillText(input.footer, pad, H - 56);
  ctx.fillStyle = '#5b6b80';
  ctx.font = `500 20px ${SANS}`;
  const credit = input.mapLib === 'mapbox' ? '© Mapbox © OpenStreetMap' : '© CARTO © OpenStreetMap';
  ctx.textAlign = 'right';
  ctx.fillText(credit, W - pad, H - 60);
  ctx.textAlign = 'left';

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not create the image'))), 'image/png'));
}

/** Shortens a line with … so it fits the width. */
function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
}
